import React from 'react';
import {
    Paper,
    Box,
    Typography,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TextField,
    Avatar,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import useCurrency from '../../../hooks/common/useCurrency';

/**
 * Renders the requested items for a return.
 *
 * @param {Array}    items                Return items.
 * @param {string}   status               The current return status.
 * @param {object}   [editableQuantities] Map of itemId -> approved_qty (controlled).
 * @param {function} [onApprovedQtyChange] (itemId, newQty) - only called when editable.
 */
const ReturnItemsTable = ({
    items = [],
    status,
    editableQuantities,
    onApprovedQtyChange,
}) => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');
    const { format: formatPrice } = useCurrency();

    const editable = status === 'pending' && typeof onApprovedQtyChange === 'function';

    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 2, md: 3 },
                borderRadius: 3,
                border: (t) => `1px solid ${t.palette.divider}`,
                mb: 3,
            }}
        >
            <Typography variant="h6" fontWeight={700} mb={2}>
                {isArabic ? 'المنتجات المطلوبة للإرجاع' : 'Requested Items'}
            </Typography>

            {items.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    {isArabic ? 'لا توجد عناصر' : 'No items'}
                </Typography>
            ) : (
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>{isArabic ? 'المنتج' : 'Product'}</TableCell>
                            <TableCell align="center">{isArabic ? 'الكمية المطلوبة' : 'Requested Qty'}</TableCell>
                            <TableCell align="center">
                                {isArabic ? 'الكمية الموافق عليها' : 'Approved Qty'}
                            </TableCell>
                            <TableCell>{isArabic ? 'السبب' : 'Reason'}</TableCell>
                            <TableCell align="end">{isArabic ? 'المبلغ' : 'Amount'}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {items.map((item) => {
                            const productName = item.product?.name?.[i18n.language]
                                || item.product?.name?.en
                                || item.product_name
                                || '-';
                            const sku = item.variant?.sku || item.sku || item.product?.sku;
                            const requestedQty = item.qty ?? item.requested_qty ?? 0;
                            const approvedQty = editable
                                ? (editableQuantities?.[item.id] ?? requestedQty)
                                : (item.approved_qty ?? requestedQty);

                            return (
                                <TableRow key={item.id}>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Avatar
                                                src={item.product?.image?.thumb || item.product?.image_url}
                                                variant="rounded"
                                                sx={{ width: 40, height: 40 }}
                                            />
                                            <Box>
                                                <Typography variant="body2" fontWeight="medium">
                                                    {productName}
                                                </Typography>
                                                {sku && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        SKU: {sku}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </TableCell>

                                    <TableCell align="center">
                                        <Typography variant="body2">{requestedQty}</Typography>
                                    </TableCell>

                                    <TableCell align="center">
                                        {editable ? (
                                            <TextField
                                                type="number"
                                                size="small"
                                                value={approvedQty}
                                                onChange={(e) =>
                                                    onApprovedQtyChange(
                                                        item.id,
                                                        Math.max(0, Math.min(requestedQty, parseInt(e.target.value || 0, 10)))
                                                    )
                                                }
                                                inputProps={{ min: 0, max: requestedQty }}
                                                sx={{ width: 80 }}
                                            />
                                        ) : (
                                            <Typography variant="body2" fontWeight="medium">
                                                {approvedQty}
                                            </Typography>
                                        )}
                                    </TableCell>

                                    <TableCell>
                                        <Typography variant="body2" color="text.secondary">
                                            {item.reason || '-'}
                                        </Typography>
                                    </TableCell>

                                    <TableCell align="end">
                                        <Typography variant="body2" fontWeight="medium">
                                            {formatPrice(item.line_total ?? item.refund_amount ?? 0)}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            )}
        </Paper>
    );
};

export default ReturnItemsTable;
